import { createFileRoute } from '@tanstack/react-router'
import { Prose } from '@/components/Prose'

export const Route = createFileRoute('/_layout/privacy-policy')({
  component: PrivacyPage,
})

function PrivacyPage() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-16">
      <Prose>
        <h1 className="text-text-primary">Privacy Policy</h1>
        <p className="text-text-muted text-sm">Last updated: August 2026</p>

        <p>
          Your cycle data is sensitive. This policy explains, in plain language, exactly what Lunara
          does and does not collect, where it is stored, and the choices you have. The short
          version: by default, <strong>your data stays on your device and is never uploaded</strong>{' '}
          — and if you opt in to sync,{' '}
          <strong>it is end-to-end encrypted so we cannot read it</strong>.
        </p>

        <h2 className="text-text-primary">1. The default: local-first, no account</h2>
        <p>
          When you use the tracker without signing in, Lunara stores your periods, symptoms,
          settings and predictions only in your browser using IndexedDB. This data never leaves your
          device, is not transmitted over the network, and works fully offline. Lunara does not run
          analytics or advertising scripts on the tracker, login, or dashboard pages.
        </p>

        <h2 className="text-text-primary">2. Accounts and authentication</h2>
        <p>
          Creating an account is optional and only needed for encrypted cross-device sync. If you
          register, we store your email address, a name, and a salted, hashed password (bcrypt). We
          keep a session cookie in your browser so you stay signed in. We do not collect or store
          your cycle data in readable form on our servers.
        </p>

        <h2 className="text-text-primary">3. Encrypted sync (end-to-end)</h2>
        <p>
          If you turn on sync, Lunara encrypts your data on your device before uploading it. The
          server stores only ciphertext and wrapped (encrypted) keys — specifically: wrapped data
          key, wrapped recovery key, the encrypted vault, initialization vectors, key-derivation
          salts, iteration count, a version number, and a timestamp. We do not have your password or
          your recovery code, and we cannot decrypt your data.
        </p>
        <p>
          <strong>
            If you forget your password and lose your recovery code, your synced data is permanently
            unrecoverable.
          </strong>{' '}
          This is the trade-off of true end-to-end encryption, and it is the reason we cannot read
          your data.
        </p>

        <h2 className="text-text-primary">4. Analytics and advertising</h2>
        <p>
          We use Google Analytics and may show advertising on{' '}
          <strong>public content pages only</strong> (such as this one and our guides). These
          scripts are not loaded when you are in the tracker, login, or dashboard. We never sell
          personal data. Cycle data, whether local or synced, is never used for advertising or
          shared with advertising partners.
        </p>

        <h2 className="text-text-primary">5. Cookies</h2>
        <p>
          We use a strictly necessary session cookie when you sign in. On public content pages,
          Google Analytics and any advertising may set their own cookies. You can control cookies
          through your browser settings.
        </p>

        <h2 className="text-text-primary">6. Third parties and data residency</h2>
        <p>
          Lunara is hosted on Cloudflare (static pages, Workers API, and D1 database), which may
          process and store data across its global network. If you use Google sign-in, Google
          processes that authentication. Cycle data uploaded for sync is stored as ciphertext in
          Cloudflare D1.
        </p>

        <h2 className="text-text-primary">7. Your rights (GDPR / CCPA and similar)</h2>
        <p>
          You can access and export your local data at any time from the tracker, and delete it by
          clearing browser storage or using the &ldquo;erase everything&rdquo; action. For account
          data and synced ciphertext, email{' '}
          <a href="mailto:privacy@lunara.app">privacy@lunara.app</a> to request access or deletion.
          Because synced data is end-to-end encrypted, we can delete ciphertext on request but
          cannot read it for you.
        </p>

        <h2 className="text-text-primary">8. Children</h2>
        <p>
          Lunara is not intended for users under the age at which they can lawfully consent to
          health data processing in their jurisdiction, and is not directed at young children.
        </p>

        <h2 className="text-text-primary">9. Changes</h2>
        <p>
          We may update this policy. Material changes will be reflected by the &ldquo;last
          updated&rdquo; date above. For questions, email{' '}
          <a href="mailto:privacy@lunara.app">privacy@lunara.app</a>.
        </p>

        <p className="text-sm">
          Lunara is an educational tool and not a medical device or contraceptive. See our{' '}
          <a href="/terms">Terms of Service</a>.
        </p>
      </Prose>
    </section>
  )
}
